import React, { useCallback, useMemo } from "react"
import { Button, Collapse, Descriptions, Tag, Row, Col, message, Space, Typography } from "antd"
import { UserOutlined, FileTextOutlined, TableOutlined, DownloadOutlined } from "@ant-design/icons"
import { ISapAsset, ISapAssetDetail } from "@/types/sapAsset.type"
import AssetPreviewCard from "./AssetPreviewCard"
import ApprovalInfo from "@/atoms/approval-info"
import { IApprovalProccessId } from "@/types"
import { getAssetColumns } from "../utils/assetColumns"
import { exportStyledExcel, IExcelExportColumn } from "../../shared/utils/exportStyledExcel"

const { Panel } = Collapse
const { Text, Link } = Typography

interface ISapSanitizedAsset extends Omit<ISapAsset, "approval_process_id"> {
  approval_process_id?: {
    status: number
    data: IApprovalProccessId
  }
  detail?: ISapAssetDetail[]
}

interface Props {
  data: ISapSanitizedAsset
}

const DetailSection: React.FC<{ title: React.ReactNode; icon: React.ReactNode; children: React.ReactNode; extra?: React.ReactNode }> = ({ title, icon, children, extra }) => {
  return (
    <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
      <Panel header={<span>{icon} <span style={{ marginLeft: 8 }}>{title}</span></span>} key="1" extra={extra}>
        {children}
      </Panel>
    </Collapse>
  )
}

const SapAssetDetailView: React.FC<Props> = ({ data }) => {
  const exportRows = useMemo(() => data.detail || [], [data.detail])
  const exportColumns = useMemo(
    () =>
      getAssetColumns(data.template_key, exportRows).map<IExcelExportColumn<ISapAssetDetail>>(column => ({
        key: column.key,
        label: column.label,
        type: ["quantity", "unit_price", "planned_usage_years"].includes(column.key)
          ? "number"
          : ["asset_capitalization_date", "depreciation_calculation_start_date", "start_use_date"].includes(column.key)
            ? "date"
            : "string",
      })),
    [data.template_key, exportRows],
  )

  const getStatusColor = useCallback((status?: string) => {
    switch (status) {
      case "Approved":
        return "green"
      case "Reject":
      case "Cancel":
        return "red"
      case "Waiting Approval":
        return "blue"
      default:
        return "blue"
    }
  }, [])

  const handleExport = useCallback(async () => {
    try {
      await exportStyledExcel({
        filename: `SAP_Asset_${data.record_id || "detail"}.xlsx`,
        sheetName: "Asset Data",
        title: `SAP Asset - ${data.record_id || "-"}`,
        columns: exportColumns,
        rows: exportRows,
      })
    } catch (_error) {
      void message.error("Failed to export asset Excel")
    }
  }, [data.record_id, exportColumns, exportRows])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      void message.success("Asset link copied")
    } catch (_error) {
      void message.error("Failed to copy asset link")
    }
  }, [])

  return (
    <>
      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="User Information" icon={<UserOutlined />}>
            <Descriptions column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Employee ID">{data.employee_id}</Descriptions.Item>
              <Descriptions.Item label="Fullname">{data.fullname}</Descriptions.Item>
              <Descriptions.Item label="Level">{data.level || "-"}</Descriptions.Item>
              <Descriptions.Item label="Department">{data.department || "-"}</Descriptions.Item>
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="Request Details" icon={<FileTextOutlined />}>
            <Descriptions column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Record ID">
                {data.record_id ? (
                  <Space size={8}>
                    <Text>{data.record_id}</Text>
                    <Link onClick={() => void handleCopyLink()}>Click link to copy</Link>
                  </Space>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Type of Request">{data.type_of_request}</Descriptions.Item>
              <Descriptions.Item label="Approval Status">
                <Tag color={getStatusColor(data.approval_status)}>{data.approval_status || "-"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Note">{data.note || "-"}</Descriptions.Item>
              <Descriptions.Item label="Created At">{data.created_at ? new Date(data.created_at).toLocaleString() : "-"}</Descriptions.Item>
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection
            title="Asset Data"
            icon={<TableOutlined />}
            extra={exportRows.length ? (
              <div onClick={event => event.stopPropagation()}>
                <Button icon={<DownloadOutlined />} onClick={() => void handleExport()}>
                  Export Data Updated
                </Button>
              </div>
            ) : undefined}
          >
            <AssetPreviewCard items={data.detail || []} templateKey={data.template_key} />
          </DetailSection>
        </Col>
      </Row>

      {data.approval_process_id && (
        <Row gutter={24}>
          <Col xs={24}>
            <DetailSection title="Approval Info & Comments" icon={<FileTextOutlined />}>
              <ApprovalInfo documentData={data} usePanel={false} />
            </DetailSection>
          </Col>
        </Row>
      )}
    </>
  )
}

export default SapAssetDetailView
